import React from "react";

import {
	type DashboardListVariable,
	type DashboardStaticListVariable,
	sortDashboardListValues,
} from "../../../../common/dashboard-spec";
import { Select } from "../../../../components/_select";

import { IncludeEverythingSwitch } from "./_include-everything-switch";

type SingleChoiceVariable = Exclude<DashboardListVariable, { allowMultiple: true }>;

interface Props extends Pick<SingleChoiceVariable, "allowAllValue" | "sort" | "defaultValue"> {
	onChange: (newValue: SingleChoiceVariable["defaultValue"]) => void;
	onDropdownVisibleChange?: (visible: boolean) => void;
	options: DashboardStaticListVariable["values"];
}

export const ListSingleChoiceVariable = ({
	allowAllValue,
	defaultValue,
	onChange,
	onDropdownVisibleChange,
	options,
	sort,
}: Props): React.JSX.Element => {
	const [previousValue, setPreviousValue] = React.useState("");
	const isEverythingSelected = defaultValue?.type === "Everything";
	const value = isEverythingSelected ? previousValue : (defaultValue?.value ?? "");

	const sortedOptions = React.useMemo(
		() =>
			sortDashboardListValues(options, sort).map((option) => ({
				value: option.value.toString(),
				label: option.label.toString(),
			})),
		[options, sort],
	);

	return (
		<>
			<Select
				allowArbitraryValues
				className="w-full"
				disabled={isEverythingSelected}
				filterBy={["label", "value"]}
				notFoundContent="No results found"
				onChange={(newValue: string) => onChange({ type: "Value", value: newValue })}
				onDropdownVisibleChange={onDropdownVisibleChange}
				options={sortedOptions}
				value={value === "" ? undefined : value} // Empty string is rendered as "" in the textbox
				virtual
			/>

			{allowAllValue && (
				<IncludeEverythingSwitch
					checked={defaultValue?.type === "Everything"}
					onChange={(checked) => {
						if (checked) {
							setPreviousValue(value);
							onChange({ type: "Everything" });
						} else {
							onChange({ type: "Value", value: previousValue });
						}
					}}
				/>
			)}
		</>
	);
};

export type ListSingleChoiceVariableProps = Props;
